<script setup lang="ts">

    // 1. Imports
    import { getContrastTextColor } from '@chatbot/utils/color';

    // 2. Props
    const props = defineProps<{
        primaryColor?: string;
        isDisabled?: boolean;
    }>();

    // 3. Emits
    const emit = defineEmits<{
        send: [message: string];
    }>();

    // 4. Composables
    const { t } = useI18n();

    // 5. Computed properties
    const textColor = computed<string>(() => getContrastTextColor(props.primaryColor ?? '#00453a'));

    // 6. Refs / Reactive state
    const inputMessage = ref<string>('');

    // 7. Methods / Functions
    const sendMessage = (): void => {
        if (props.isDisabled) return;

        const text = inputMessage.value.trim();
        if (!text) return;

        emit('send', text);
        inputMessage.value = '';
    };

    const handleKeypress = (evt: KeyboardEvent): void => {
        if (evt.key === 'Enter' && !evt.shiftKey) {
            evt.preventDefault();
            if (!props.isDisabled) {
                sendMessage();
            }
        }
    };
</script>

<template>
    <div
        class="input-container"
        :style="{ '--send-button-text-color': textColor }"
    >
        <input
            v-model="inputMessage"
            type="text"
            :placeholder="t('chat.inputPlaceholder')"
            class="message-input"
            @keypress="handleKeypress"
        >
        <button
            type="button"
            class="send-button"
            :disabled="!inputMessage.trim() || isDisabled"
            @click="sendMessage"
        >
            <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
            >
                <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
            </svg>
        </button>
    </div>
</template>

<style scoped lang="scss">
.input-container {
    display: flex;
    padding: calc(var(--spacing-base) * 5);
    background: var(--background-secondary-highlight);
    border-block-start: 1px solid var(--border-quaternary);
    gap: var(--spacing-3x);
}

.message-input {
    flex: 1;
    border: 2px solid var(--border-quaternary);
    border-radius: var(--border-radius-rounded);
    font-size: var(--font-medium);
    outline: none;
    padding-block: var(--spacing-3x);
    padding-inline: var(--spacing-03);
    transition: border-color 0.2s ease;
}

.message-input:focus {
    border-color: var(--chat-primary-color);
}

.message-input:disabled {
    background-color: rgb(0 0 0 / 0.05);
    cursor: not-allowed;
    opacity: 0.6;
}

.send-button {
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    border-radius: var(--border-radius-circle);
    background: var(--chat-primary-color);
    block-size: var(--size-9);
    color: var(--send-button-text-color);
    cursor: pointer;
    inline-size: var(--size-9);
    transition:
        transform 0.2s ease,
        opacity 0.2s ease;
}

.send-button:disabled {
    cursor: not-allowed;
    opacity: 0.5;
}

.send-button:hover:not(:disabled) {
    transform: scale(1.05);
}

.send-button:active:not(:disabled) {
    transform: scale(0.95);
}
</style>
